import { ReactNode, createContext, useContext, useEffect, useMemo, useState } from "react";
import { registerStoreUpdater } from "../../utils/shadowSession";

// Encryption key - in production, this should be more secure or derived from user credentials
const ENCRYPTION_KEY = "your-secret-key-here-change-this";

async function encryptData(data: string): Promise<string> {
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(data);

  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    encoder.encode(ENCRYPTION_KEY),
    { name: "PBKDF2" },
    false,
    ["deriveBits", "deriveKey"]
  );

  const salt = crypto.getRandomValues(new Uint8Array(16));
  const key = await crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: salt,
      iterations: 100000,
      hash: "SHA-256"
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt"]
  );

  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encryptedData = await crypto.subtle.encrypt({ name: "AES-GCM", iv: iv }, key, dataBuffer);

  const combined = new Uint8Array(salt.length + iv.length + encryptedData.byteLength);
  combined.set(salt, 0);
  combined.set(iv, salt.length);
  combined.set(new Uint8Array(encryptedData), salt.length + iv.length);

  return btoa(Array.from(combined, byte => String.fromCharCode(byte)).join(""));
}

async function decryptData(encryptedData: string): Promise<string> {
  try {
    const encoder = new TextEncoder();
    const combined = Uint8Array.from(atob(encryptedData), c => c.charCodeAt(0));

    const salt = combined.slice(0, 16);
    const iv = combined.slice(16, 28);
    const data = combined.slice(28);

    const keyMaterial = await crypto.subtle.importKey(
      "raw",
      encoder.encode(ENCRYPTION_KEY),
      { name: "PBKDF2" },
      false,
      ["deriveBits", "deriveKey"]
    );

    const key = await crypto.subtle.deriveKey(
      {
        name: "PBKDF2",
        salt: salt,
        iterations: 100000,
        hash: "SHA-256"
      },
      keyMaterial,
      { name: "AES-GCM", length: 256 },
      false,
      ["decrypt"]
    );

    const decryptedData = await crypto.subtle.decrypt({ name: "AES-GCM", iv: iv }, key, data);

    const decoder = new TextDecoder();
    return decoder.decode(decryptedData);
  } catch (error) {
    console.error("Decryption failed:", error);
    throw error;
  }
}

function isEncrypted(data: string): boolean {
  try {
    // Try to parse as JSON - if it works, it's not encrypted
    JSON.parse(data);
    return false;
  } catch {
    // If it's not valid JSON, assume it's encrypted (base64 string)
    return true;
  }
}

// eslint-disable-next-line no-unused-vars
const UserContext = createContext<{ store: any; setStore: (user: any) => void } | undefined>(
  undefined
);

export function UserProvider({ children }: { children: ReactNode }) {
  const [store, setStore] = useState<any>(() => {
    const storedData = localStorage.getItem("data");
    if (!storedData) return null;

    // Check if data is encrypted
    if (isEncrypted(storedData)) {
      // We can't use async in useState initializer, so return null and load async
      return null;
    } else {
      // Legacy unencrypted data
      return JSON.parse(storedData);
    }
  });

  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Load encrypted data on mount
  useEffect(() => {
    async function loadEncryptedData() {
      const storedData = localStorage.getItem("data");
      if (storedData && isEncrypted(storedData)) {
        try {
          const decrypted = await decryptData(storedData);
          setStore(JSON.parse(decrypted));
        } catch (error) {
          console.error("Failed to decrypt stored data:", error);
          localStorage.removeItem("data");
        }
      }
      setIsLoading(false);
    }

    loadEncryptedData();
  }, []);

  // Save encrypted data
  useEffect(() => {
    async function saveEncryptedData() {
      if (store) {
        try {
          const encrypted = await encryptData(JSON.stringify(store));
          localStorage.setItem("data", encrypted);
        } catch (error) {
          console.error("Failed to encrypt data:", error);
        }
      } else {
        localStorage.removeItem("data");
      }
    }

    if (!isLoading) {
      saveEncryptedData();
    }
  }, [store, isLoading]);

  // Let non-React code end a shadow session. The request layer is where an
  // expired shadow token first appears, as a 401, and it has no context to
  // reach for — without this its only option is the login screen.
  useEffect(() => {
    registerStoreUpdater(setStore);
  }, [setStore]);

  const contextValue = useMemo(() => ({ store, setStore }), [store, setStore]);

  return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>;
}

export function useStore() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("Store must be used within a UserProvider");
  }
  return context;
}
